import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useConsentStore } from '@/stores/consentStore';
import ConsentBanner from '@/components/layout/ConsentBanner';
import ConsentPreferencesModal from '@/components/layout/ConsentPreferencesModal';
import { Loader2 } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { initializeAllTrackersBasedOnConsent } from '@/lib/tracking';

// Keep the landing page in the initial bundle; route-specific content loads on demand.
import HomePage from './pages/HomePage';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Lazy Loaded Resources
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const DanielGaskinsPage = lazy(() => import('./pages/DanielGaskinsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ToolsIndexPage = lazy(() => import('./pages/tools/ToolsIndexPage')); // NEW
const FineCalculatorPage = lazy(() => import('./pages/tools/FineCalculatorPage'));
const RainfallHistoryPage = lazy(() => import('./pages/tools/RainfallHistoryPage'));
const RunoffCalculatorPage = lazy(() => import('./pages/tools/RunoffCalculatorPage'));
const MapSketcherPage = lazy(() => import('./pages/tools/MapSketcherPage'));
const SwpppAuditPage = lazy(() => import('./pages/tools/SwpppAuditPage'));
const CGP2025Page = lazy(() => import('./pages/resources/CGP2025Page'));
const VegetationDensityPage = lazy(() => import('./pages/resources/VegetationDensityPage'));
const ToolboxTalksPage = lazy(() => import('./pages/resources/ToolboxTalksPage'));
const StateIndexPage = lazy(() => import('./pages/resources/StateIndexPage'));
const StateGuidePage = lazy(() => import('./pages/resources/StateGuidePage'));
const BMPIndexPage = lazy(() => import('./pages/resources/bmp/BMPIndexPage'));
const BMPDetailPage = lazy(() => import('./pages/resources/bmp/BMPDetailPage'));
const TemplatesPage = lazy(() => import('./pages/resources/TemplatesPage'));
const ForConsultantsPage = lazy(() => import('./pages/solutions/ForConsultantsPage'));
const SampleInspectionPage = lazy(() => import('./pages/demo/SampleInspectionPage'));

const queryClient = new QueryClient();

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const AppLayout: React.FC<{ children: React.ReactNode; isClient: boolean }> = ({ children, isClient }) => (
  <div className="flex flex-col min-h-screen selection:bg-blue-500/30">
    <Suspense fallback={<div className="h-16"></div>}>{isClient && <Header />}</Suspense>
    <main className="flex-grow" id="main-content">
      <Suspense fallback={<div className="flex min-h-[60vh] items-center justify-center" role="status"><Loader2 className="h-8 w-8 animate-spin text-primary" /><span className="sr-only">Loading page</span></div>}>
        {children}
      </Suspense>
    </main>
    <ConsentBanner />
    <ConsentPreferencesModal />
    <Suspense fallback={<div></div>}>{isClient && <Footer />}</Suspense>
  </div>
);

const SuspenseFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
    <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
  </div>
);

function App() {
  const theme = useAppStore((state) => state.theme);
  const initializeConsents = useConsentStore((state) => state.initializeConsents);
  const consentInitAttempted = useRef(false);
  const isClient = true;

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem('saas-starter-marketing-theme');
      if (!stored) { 
        useAppStore.getState().setTheme(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => { 
    if (!isClient) return;
    let isMounted = true;

    const unsubscribe = useConsentStore.subscribe(
      (state, prevState) => {
        if ((state.hasMadeChoice && state.hasMadeChoice !== prevState.hasMadeChoice) ||
            (state.consent.analytics === 'granted' && prevState.consent.analytics !== 'granted')) {
            initializeAllTrackersBasedOnConsent();
        }
      }
    );
    
    const fetchJurisdictionAndInitConsent = async () => {
      if (consentInitAttempted.current) return;
      consentInitAttempted.current = true;
      const { hasMadeChoice } = useConsentStore.getState();
      if (hasMadeChoice) {
          await initializeAllTrackersBasedOnConsent();
          return;
      }
      if (isMounted) initializeConsents('UNKNOWN');
    };
    
    fetchJurisdictionAndInitConsent();
    return () => { isMounted = false; unsubscribe(); };
  }, [initializeConsents, isClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ScrollToTop />
        {isClient ? (
        <Routes>
          <Route path="/" element={<AppLayout isClient={isClient}><HomePage /></AppLayout>} />
          
          {/* Solutions */}
          <Route path="/solutions/for-consultants" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><ForConsultantsPage/></Suspense></AppLayout>} />

          {/* Tools & Resources */}
          <Route path="/tools" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><ToolsIndexPage/></Suspense></AppLayout>} />
          <Route path="/tools/fine-calculator" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><FineCalculatorPage/></Suspense></AppLayout>} />
          <Route path="/tools/rain-event-finder" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><RainfallHistoryPage/></Suspense></AppLayout>} /> 
          <Route path="/tools/runoff-calculator" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><RunoffCalculatorPage/></Suspense></AppLayout>} />
          <Route path="/tools/swppp-audit" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><SwpppAuditPage/></Suspense></AppLayout>} />
          <Route path="/tools/map-sketcher" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><MapSketcherPage/></Suspense></AppLayout>} />
          <Route path="/demo/sample-inspection" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><SampleInspectionPage/></Suspense></AppLayout>} />
          
          <Route path="/resources/templates" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><TemplatesPage/></Suspense></AppLayout>} />
          <Route path="/resources/toolbox-talks" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><ToolboxTalksPage/></Suspense></AppLayout>} />
          <Route path="/resources/vegetation-density" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><VegetationDensityPage/></Suspense></AppLayout>} />
          <Route path="/resources/2025-cgp-update" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><CGP2025Page/></Suspense></AppLayout>} />

          {/* Programmatic SEO Routes */}
          <Route path="/swppp-requirements" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><StateIndexPage/></Suspense></AppLayout>} />
          <Route path="/swppp-requirements/:stateSlug" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><StateGuidePage/></Suspense></AppLayout>} />
          <Route path="/resources/bmp-field-guide" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><BMPIndexPage/></Suspense></AppLayout>} />
          <Route path="/resources/bmp-field-guide/:slug" element={<AppLayout isClient={isClient}><Suspense fallback={<SuspenseFallback/>}><BMPDetailPage/></Suspense></AppLayout>} />

          <Route path="/features" element={<AppLayout isClient={isClient}><FeaturesPage /></AppLayout>} />
          <Route path="/pricing" element={<AppLayout isClient={isClient}><PricingPage /></AppLayout>} />
          <Route path="/blog" element={<AppLayout isClient={isClient}><BlogIndexPage /></AppLayout>} />
          <Route path="/blog/:slug" element={<AppLayout isClient={isClient}><BlogPostPage /></AppLayout>} />
          
          <Route path="/privacy-policy" element={<AppLayout isClient={isClient}><PrivacyPolicyPage /></AppLayout>} />
          <Route path="/terms-of-service" element={<AppLayout isClient={isClient}><TermsOfServicePage /></AppLayout>} />
          <Route path="/cookie-policy" element={<AppLayout isClient={isClient}><CookiePolicyPage /></AppLayout>} />
          <Route path="/faq" element={<AppLayout isClient={isClient}><FaqPage /></AppLayout>} />
          <Route path="/support" element={<AppLayout isClient={isClient}><SupportPage /></AppLayout>} />
          <Route path="/contact" element={<AppLayout isClient={isClient}><ContactPage /></AppLayout>} />
          <Route path="/about/daniel-gaskins" element={<AppLayout isClient={isClient}><DanielGaskinsPage /></AppLayout>} />
          <Route path="*" element={<AppLayout isClient={isClient}><NotFoundPage /></AppLayout>} /> 
        </Routes>
        ) : (
          <AppLayout isClient={isClient}><SuspenseFallback /></AppLayout>
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
